import { defaultTo, symmetricDifference } from "ramda";
import { type ForwardedRef, type MutableRefObject, useCallback, useEffect, useMemo, useRef } from "react";

import { useForwardedRef } from "./useForwardedRef.ts";

type KeyboardEventHandler = (event: KeyboardEvent) => void;

/**
 * @see https://github.com/reecelucas/react-use-hotkeys
 * @see https://github.com/palantir/blueprint/blob/develop/packages/core/src/hooks/hotkeys/useHotkeys.ts (v4)
 */

// After KEY_SEQUENCE_TIMEOUT we consider the keystroke sequence invalid
const KEY_SEQUENCE_TIMEOUT = 1000;

// Use ESCAPE_HATCH_KEY ot react to all keystrokes
const ESCAPE_HATCH_KEY = "*";

// Support of Mac
const IS_MAC = navigator.platform.toLowerCase().includes("mac");

export interface HotKeysOptions {
  /** @default true */
  preventDefault?: boolean;
  /** @default false */
  continuePropagation?: boolean;
  /** @default true */
  convertForMac?: boolean;
  /** @default false */
  disabled?: boolean;
  /** @default false */
  activeOnEditable?: boolean;
}

/**
 * Define an application wide keyboard shortcut
 * Supports Mac by default:
 *   - Use "Control" for the Command/Meta key on Mac and the usual Control key on PC
 *   - Use "Ctrl" for the ctrl key on Mac
 * If you explicitly deactivate the Mac support, use the Js key names: "Control" and "Meta"
 *
 * @param keys Separate Keys by a "+" sign or a space. To use the space bar, you can also use " " (Shift+" ")
 * @param callback Usual Keyboard Event Handler
 * @param ref A React DOM reference to attach the event to. If undefined, we attach the event to the window.
 *            Set the ref to null to force it to be local
 * @param continuePropagation Continue propagation of the keyboard event
 * @param convertForMac Mac support to convert "Meta" to "Control" like in PC (the usual way of using Mac keyboards)
 */
const useHotKeys = (
  keys: string,
  callback: KeyboardEventHandler,
  ref?: MutableRefObject<Element> | ForwardedRef<Element>,
  options?: HotKeysOptions,
) => {
  const { preventDefault, continuePropagation, convertForMac, activeOnEditable, disabled }: HotKeysOptions = {
    preventDefault: defaultTo(true)(options?.preventDefault),
    continuePropagation: defaultTo(false)(options?.continuePropagation),
    convertForMac: defaultTo(true)(options?.convertForMac),
    disabled: defaultTo(false)(options?.disabled),
    activeOnEditable: defaultTo(false)(options?.activeOnEditable),
  };

  // Make ref compatible with old and new ref/forwardRef code
  const fRef = useForwardedRef(ref);
  const hotKeysListRef = useRef<Map<string, Element | (Window & typeof globalThis)>>(new Map());

  // The same callback as defined + preventDefault & stopPropagation
  const cb = useCallback(
    (event: KeyboardEvent) => {
      callback(event);

      // Usually we don't want something else to happen next
      if (preventDefault) event.preventDefault();
      if (!continuePropagation) event.stopPropagation();
    },
    [callback, preventDefault, continuePropagation],
  );

  const trimmedKeys = useMemo(() => keys.trim(), [keys]);

  // Parse the keys string
  const hotkeysArray = useMemo(() => getHotkeysArray(trimmedKeys), [trimmedKeys]);

  // Get the onKeydown event
  const onKeyDown = useMemo(
    () => getOnKeyDown(hotkeysArray, cb, convertForMac, activeOnEditable),
    [cb, convertForMac, hotkeysArray, activeOnEditable],
  );

  useEffect(() => {
    // undefined, null or ""
    if (!trimmedKeys || disabled) return;

    // ref may be null. React needs to render first.
    const element = ref == null ? window : fRef.current;
    const keyDownCallback = onKeyDown;

    if (hotKeysListRef.current.has(trimmedKeys)) {
      if (import.meta.env.DEV) {
        console.warn(`HotKeys ${trimmedKeys} has already been set for ${element}`);
      }
    } else if (element) {
      hotKeysListRef.current.set(trimmedKeys, element);
      element.addEventListener("keydown", keyDownCallback as EventListener, { capture: true });
    }

    return () => {
      const dictKey = hotKeysListRef.current.get(trimmedKeys);
      if (element) {
        // This is ok to call the modified current ref here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (dictKey === element) hotKeysListRef.current.delete(trimmedKeys);
        element.removeEventListener("keydown", keyDownCallback as EventListener, { capture: true });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fRef, onKeyDown, trimmedKeys, disabled]);
};

// Utilities
const getOnKeyDown = (
  hotkeysArray: string[],
  callback: KeyboardEventHandler,
  convertForMac: boolean,
  activeOnEditable: boolean,
) => {
  let sequenceTimer: number;
  let keySequence: string[] = [];

  // Used to check the keystrokes in order and compare it to the keys string defined in the hook
  const handleKeySequence = (event: KeyboardEvent, hotkeys: string[]) => {
    clearTimeout(sequenceTimer);

    // The keystrokes sequence becomes invalid after KEY_SEQUENCE_TIMEOUT
    sequenceTimer = window.setTimeout(() => {
      keySequence = []; // reset
    }, KEY_SEQUENCE_TIMEOUT);

    // Add new keystroke to the registered sequence
    keySequence.push(event.key.toLowerCase());

    // Check keystrokes in order
    if (isArraysEqualInOrder(keySequence, hotkeys)) {
      keySequence = []; // reset
      clearTimeout(sequenceTimer);
      callback(event);
    }
  };

  // Used when modifier keys (Control, Shift...) are used
  const handleModifierCombo = (event: KeyboardEvent, hotkeys: string[]) => {
    // Clone and Remove the last item, the action key. We will keep the modKeys only (control, shift...)
    const modKeys = [...hotkeys];
    const actionKey = modKeys.pop();

    // Get currently pressed modKeys
    const pressedModKeys = getPressedModifierKeys(event, convertForMac);

    // Compare if modKeys arrays matches, not necessarily in the same order
    // We don't compare the length, as [Control, Control] === [Control]. We don't care about double modKeys.
    const isAllModKeysPressed = symmetricDifference(pressedModKeys, modKeys).length === 0;

    // Check that the last keystroke of the sequence is the correct sign, like in Control+Shift+C
    if (isAllModKeysPressed && event.key.toLowerCase() === actionKey?.toLowerCase()) {
      callback(event);
    }
  };

  // Return the onKeydown event
  return (event: KeyboardEvent) => {
    // Chrome autocomplete triggers `keydown` event but event.key will be undefined
    // @see https://bugs.chromium.org/p/chromium/issues/detail?id=581537.
    if (!event.key && !modifierKeyPressed(event)) return;

    // If the focus is on an editable element, we do nothing
    if (isTargetEditable(event) && !activeOnEditable) return;

    // Provide escape hatch so that the user can perform
    // some custom logic not supported by the API.
    if (hotkeysArray.length === 1 && hotkeysArray[0] === ESCAPE_HATCH_KEY) {
      callback(event);
      return;
    }

    if (modifierKeyPressed(event)) {
      // Handle modifier key combos
      handleModifierCombo(event, hotkeysArray);
    } else {
      if (hotkeysArray.length > 1) {
        // Handle key sequences
        handleKeySequence(event, hotkeysArray);
      } else if (event.key.toLowerCase() === hotkeysArray[0].toLowerCase()) {
        // Handle the basic case; a single hotkey
        callback(event);
      }
    }
  };
};

// Check is the current active element is not an editable element (input, textarea...)
// If the current active element is editable, we don't execute the shortcut
const isTargetEditable = (event: KeyboardEvent) => {
  const activeElement = event.target as HTMLElement;

  // Check if the parent is not editable neither (like an icon in a input...)
  const editable = activeElement.closest("input, textarea, [contenteditable=true]");

  if (editable == null || editable.getAttribute("aria-readonly") === "true") {
    return false;
  }

  // don't let checkboxes, switches, and radio buttons prevent hotkey behavior
  if (editable.tagName.toLowerCase() === "input") {
    const inputType = (editable as HTMLInputElement).type;
    if (inputType === "checkbox" || inputType === "radio") {
      return false;
    }
  }

  // don't let read-only fields prevent hotkey behavior
  return !(editable as HTMLInputElement).readOnly;
};

// Parse keys string: "Control+V" => ["control", "v"]
const getHotkeysArray = (hotkeys: string): string[] => {
  const hkeys = hotkeys.toLowerCase();

  if (hkeys.length === 1) {
    // We're dealing with a single key
    return [hkeys];
  }

  if (hkeys.includes("+")) {
    // We're dealing with a modifier-key combination
    return hkeys.replace(/\s+/g, "").split("+");
  }

  /**
   * We're dealing with a key sequence, so split on spaces.
   * If the whitespace character is within quotation marks (" " or ' ')
   * it signifies a space key and not a delimiter.
   */
  return [...(hkeys.match(/[^\s"']+|"([^"]*)"|'([^']*)'/g) || [])].map((key) => key.replace(/(["']).*?(["'])/, " "));
};

// Control, Shift... pressed
const modifierKeyPressed = (event: KeyboardEvent): boolean =>
  event.altKey || event.ctrlKey || event.shiftKey || event.metaKey;

// Map keystroke to the corresponding keys string names
const getPressedModifierKeys = (event: KeyboardEvent, convertForMac: boolean): string[] => {
  const doMacConvert = IS_MAC && convertForMac;
  const modifiers: string[] = [];

  if (event.ctrlKey) modifiers.push(doMacConvert ? "ctrl" : "control");
  if (event.shiftKey) modifiers.push("shift");
  if (event.altKey) modifiers.push("alt");
  if (event.metaKey) modifiers.push(doMacConvert ? "control" : "meta");

  return modifiers;
};

// Compare array in order: [1, 2] != [2, 1]
const isArraysEqualInOrder = (arr1: string[], arr2: string[]) =>
  arr1.length === arr2.length && arr1.every((item, i) => item === arr2[i]);

export { useHotKeys };
